import type { Idioma } from '../../i18n/index.js';

/** Recorrido del lector: estudios, utilidad, fuentes, conexión y novedades.
 * Los modos internos son transparentes; los recuentos salen del catálogo.
 * Criterios editoriales y alcance de lanzamiento en `plan/voz-y-mensajes.md`.
 */
export interface Seccion {
  readonly titulo: string;
  readonly parrafos: readonly string[];
}

export const COMO_FUNCIONA: Readonly<Record<Idioma, readonly Seccion[]>> = {
  es: [
    {
      titulo: 'Reunimos y analizamos los estudios del sector',
      parrafos: [
        'Athena busca estudios de la industria de los eventos en distintos países y utiliza inteligencia artificial para analizar sus datos. Así puedes consultar información de diferentes fuentes sin tener que localizar y leer cada informe por separado.',
      ],
    },
    {
      titulo: 'Encuentra información para tu trabajo',
      parrafos: [
        'Cuando prepares una propuesta, un informe o una presentación, o quieras entender qué está cambiando en el sector, escribe lo que necesitas saber. Athena busca en los estudios y te responde con los datos que encuentra.',
        'Puedes explorar los temas de la portada para descubrir qué información hay disponible. Si no encontramos datos para responder a tu pregunta, te lo indicamos.',
      ],
    },
    {
      titulo: 'Consulta de dónde sale cada dato',
      parrafos: [
        'Las respuestas incluyen referencias al estudio y a la página de la que sale cada dato. Puedes abrir el documento original y, con «Copiar con la cita», llevarte una frase con su fuente para utilizarla en tu trabajo.',
        'Cada estudio tiene una ficha con quién lo publica y la información que aporta sobre cómo se hizo. Cuando se trata de una encuesta, puedes consultar a quién preguntó, cuántas personas participaron y cuándo, siempre que el estudio lo declare.',
        'Los datos pueden referirse a países, fechas y públicos distintos. Ese contexto te ayuda a valorar si sirven para tu caso. Las respuestas se generan con IA, así que conviene revisar las fuentes antes de utilizar una cifra importante.',
      ],
    },
    {
      titulo: 'Usa Athena desde ChatGPT o Claude',
      parrafos: [
        'También puedes conectar Athena con ChatGPT o Claude para consultar los estudios mientras preparas tus documentos. Crea tu cuenta y encontrarás las instrucciones de conexión en Mi cuenta.',
      ],
    },
    {
      titulo: 'Mantente al día con nuevos estudios',
      parrafos: [
        'Incorporamos nuevos estudios para ampliar la información que puedes consultar. Si quieres seguir las novedades, suscríbete para recibir por correo los nuevos estudios y las tendencias que muestran sus datos.',
      ],
    },
  ],
  en: [
    {
      titulo: 'We bring together and analyse industry studies',
      parrafos: [
        'Athena finds studies on the events industry from different countries and uses artificial intelligence to analyse their data. You can consult information from different sources without having to find and read each report separately.',
      ],
    },
    {
      titulo: 'Find information for your work',
      parrafos: [
        'When you are preparing a proposal, report or presentation, or want to understand what is changing in the industry, write what you need to know. Athena searches the studies and answers with the data it finds.',
        'Explore the topics on the home page to discover what information is available. If we cannot find data to answer your question, we tell you.',
      ],
    },
    {
      titulo: 'See where each figure comes from',
      parrafos: [
        'Answers include references to the study and the page each figure comes from. You can open the original document and use “Copy with the citation” to take a sentence with its source into your work.',
        'Each study has a record showing who publishes it and what it says about how it was carried out. For surveys, you can check who was asked, how many people took part and when, whenever the study provides that information.',
        'Data may refer to different countries, dates and groups of people. That context helps you decide whether it applies to your situation. Answers are generated with AI, so check the sources before using an important figure.',
      ],
    },
    {
      titulo: 'Use Athena from ChatGPT or Claude',
      parrafos: [
        'You can also connect Athena with ChatGPT or Claude to consult the studies while preparing your documents. Create your account and you will find the connection instructions in My account.',
      ],
    },
    {
      titulo: 'Keep up with new studies',
      parrafos: [
        'We add new studies to expand the information you can consult. Subscribe to receive new studies and the trends their data reveal by email.',
      ],
    },
  ],
};
